import { Link } from "react-router-dom";
import { useState } from "react";
import { Star, Crown, MapPin, Cigarette, Sparkles, Building2, UtensilsCrossed, Waves, ShoppingBag } from "lucide-react";
import { categoryLabel, openNowBadge } from "../lib/geo";
import { CategoryLabel } from "./CategoryLabel";
import CFImage from "./CFImage";

// Curated cinematic Havana imagery — verified URLs that render warm/moody.
const IMG_HAVANA_ROOFTOP = "https://images.unsplash.com/photo-1517457210348-703079e57d4b?w=1400&q=85&auto=format&fit=crop"; // warm Havana banner
const IMG_CIGAR_INTERIOR = "https://images.unsplash.com/photo-1574277301393-72bd4ec7a6b3?w=1400&q=85&auto=format&fit=crop"; // dim leather cigar lounge
const IMG_JAZZ_CLUB      = "https://images.unsplash.com/photo-1572116469696-31de0f17cc34?w=1400&q=85&auto=format&fit=crop"; // dim jazz club
const IMG_BAR_AMBER      = "https://images.unsplash.com/photo-1470337458703-46ad1756a187?w=1400&q=85&auto=format&fit=crop";
const IMG_RESTAURANT     = "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=1400&q=85&auto=format&fit=crop";
const IMG_HOTEL          = "https://images.unsplash.com/photo-1455587734955-081b22074882?w=1400&q=85&auto=format&fit=crop";
const IMG_BEACH          = "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=1400&q=85&auto=format&fit=crop";
const IMG_SHOP           = "https://images.unsplash.com/photo-1567521464027-f127ff144326?w=1400&q=85&auto=format&fit=crop";

const FALLBACK_IMG = {
  cigar_lounge: IMG_CIGAR_INTERIOR,
  rooftop:      IMG_HAVANA_ROOFTOP,
  club:         IMG_JAZZ_CLUB,
  bar:          IMG_BAR_AMBER,
  restaurant:   IMG_RESTAURANT,
  hotel:        IMG_HOTEL,
  beach:        IMG_BEACH,
  shop:         IMG_SHOP,
  other:        IMG_HAVANA_ROOFTOP,
};

// Normalize a stored photo URL — entries from the upload pipeline are absolute
// (`https://…/api/files/…`), entries from legacy seeds may be relative paths.
// Either way return an absolute URL or null. Skips YouTube/video slides.
const resolvePhotoUrl = (entry, key = "medium") => {
  if (!entry) return null;
  if (entry.media_type === "youtube") return null; // gallery-only; not for cards
  const raw = entry[key] || entry.medium || entry.full || entry.url;
  if (!raw) return null;
  return raw.startsWith("http") ? raw : `${process.env.REACT_APP_BACKEND_URL}${raw}`;
};

const firstImagePhoto = (b) => (b.photos || []).find((p) => p && p.media_type !== "youtube");

const photoUrl = (b) => {
  const p = firstImagePhoto(b);
  return resolvePhotoUrl(p, "medium") || (FALLBACK_IMG[b.category] || FALLBACK_IMG.other);
};

const thumbUrl = (b) => {
  const p = firstImagePhoto(b);
  return resolvePhotoUrl(p, "thumbnail") || (FALLBACK_IMG[b.category] || FALLBACK_IMG.other);
};

/**
 * iter122 — Image with offline-graceful fallback.
 * Renders the requested URL; if it errors (e.g. broken network in Cuba,
 * SW cache miss), swaps to a warm gold-on-cocoa gradient with a category
 * icon so cards never show a broken-image glyph.
 */
function CategoryGlyph({ category, className }) {
  const Icon = (() => {
    switch (category) {
      case "cigar_lounge":
      case "shop": return Cigarette;
      case "rooftop":
      case "bar":
      case "club": return Sparkles;
      case "restaurant": return UtensilsCrossed;
      case "hotel": return Building2;
      case "beach": return Waves;
      default: return ShoppingBag;
    }
  })();
  return <Icon className={className} strokeWidth={1.25} />;
}

export function OfflineImg({ src, alt, className, category, testid }) {
  const [errored, setErrored] = useState(false);
  // iter123 — Preflight: when device reports offline, skip the <img src>
  // attribute entirely. A browser-native <img> request bypasses axios
  // interceptors and can still trigger iOS's native "Cellular Data is
  // Turned Off" dialog. Rendering the gradient immediately avoids the
  // network attempt altogether.
  const isOffline = typeof navigator !== "undefined" && navigator.onLine === false;
  if (errored || !src || isOffline) {
    return (
      <div
        data-testid={testid ? `${testid}-fallback` : undefined}
        className={`${className || ""} grid place-items-center`}
        style={{ background: "linear-gradient(135deg, #1a0f06, #3a1c04)" }}
      >
        <CategoryGlyph category={category} className="w-7 h-7 text-gold" />
      </div>
    );
  }
  // iter188 — Use <CFImage> for lazy loading + dark placeholder while
  // the image streams in. Keeps the same error-fallback path (gradient
  // + category glyph) via the existing errored branch above.
  return (
    <CFImage
      src={src}
      variant="medium"
      alt={alt}
      loading="lazy"
      className={className}
      testid={testid}
    />
  );
}

// Ratings only show when ratings_average is not null (backend enforces >=3 threshold)
export function Stars({ value, size = 14 }) {
  return (
    <span className="inline-flex items-center gap-1" aria-label={`${value} stars`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          width={size}
          height={size}
          strokeWidth={1.5}
          className={n <= Math.round(value) ? "fill-[#C8A063] text-[#C8A063]" : "text-foreground/15"}
        />
      ))}
    </span>
  );
}

export function OpenBadge({ business, className = "" }) {
  const b = openNowBadge(business);
  const map = {
    open: "pill-open",
    tonight: "pill-neutral text-gold",
    closed: "pill-neutral",
  };
  return (
    <span className={`pill ${map[b.key]} ${className}`} data-testid={`open-${b.key}`}>
      {b.label}
    </span>
  );
}

/**
 * Editorial "Tonight in Havana" card — mockup spec:
 *   - 4:5 photo with VIP + OPEN NOW pills stacked top-left
 *   - Card has subtle gold border outline, deep tobacco background
 *   - Title (large serif) + category·neighborhood·distance + stars BELOW photo
 */
export function TonightCard({ b, distance }) {
  const hero = photoUrl(b);
  return (
    <Link
      to={`/places/${b.id}`}
      data-testid={`tonight-card-${b.id}`}
      className="block min-w-[76%] sm:min-w-[340px] snap-start group"
    >
      <article
        className="tonight-card relative overflow-hidden transition-all duration-500 ease-out group-hover:-translate-y-1"
        style={{
          borderRadius: 22,
          background: "linear-gradient(180deg, #1A1715 0%, #141312 100%)",
          border: "1px solid rgba(200, 160, 99, 0.18)",
          boxShadow:
            "inset 0 1px 0 rgba(255,255,255,0.025), 0 16px 36px rgba(0,0,0,0.45)",
        }}
      >
        {/* Photo fills the card; title/meta overlay so the whole card stays ≤180px on mobile */}
        <OfflineImg
          src={hero}
          alt=""
          category={b.category}
          className="tonight-card-image absolute inset-0 transition-transform duration-[1.2s] ease-out group-hover:scale-[1.05]"
        />
        {/* Dark gradient bottom-up so overlaid copy stays readable */}
        <div className="absolute inset-0 pointer-events-none"
             style={{ background: "linear-gradient(180deg, rgba(8,8,8,0) 35%, rgba(8,8,8,0.55) 65%, rgba(8,8,8,0.92) 100%)" }} />
        {/* Top-left badge stack */}
        <div className="absolute top-3 left-3 flex flex-wrap gap-1.5">
          {b.vip_enabled && (
            <span className="pill pill-vip"><Crown className="w-3 h-3" strokeWidth={2} /> VIP</span>
          )}
          {b.open_now && <OpenBadge business={b} />}
        </div>

        {/* Title + meta overlay */}
        <div className="absolute inset-x-0 bottom-0 px-4 pb-3.5 pt-6">
          <div className="font-display text-[1.25rem] sm:text-[1.5rem] leading-[1.1] text-foreground tracking-tight [text-shadow:0_2px_10px_rgba(0,0,0,0.6)] truncate">
            {b.name}
          </div>
          <div className="mt-1 text-[11px] text-foreground/70 tracking-wide flex items-center gap-1.5 [text-shadow:0_1px_6px_rgba(0,0,0,0.7)]">
            <span className="truncate">
              <CategoryLabel category={b.category} subcategory={b.subcategory} />
              {(b.neighborhood || distance) && (
                <span>{" · "}{[b.neighborhood, distance].filter(Boolean).join(" · ")}</span>
              )}
            </span>
          </div>
          {b.ratings_average != null && (
            <div className="mt-1.5 flex items-center gap-1.5">
              <Stars value={b.ratings_average} size={11} />
              <span className="text-[11px] text-foreground/85 font-mono">
                {b.ratings_average.toFixed(2)}
                <span className="text-foreground/55 ml-1">({b.approved_review_count ?? b.ratings_count ?? 0})</span>
              </span>
            </div>
          )}
        </div>
      </article>
    </Link>
  );
}

/**
 * Nearby row — square thumb left, big serif name + crown if VIP + meta + stars.
 * Mockup uses a soft separator between rows but no heavy divider lines.
 */
export function PlaceRow({ b, distance }) {
  const thumb = thumbUrl(b);
  return (
    <Link
      to={`/places/${b.id}`}
      data-testid={`place-row-${b.id}`}
      className="flex items-center gap-5 py-5 px-4 -mx-4 rounded-3xl hover:bg-[rgba(200,160,99,0.03)] transition-colors duration-300 group"
    >
      <div
        className="place-row-thumb-wrap rounded-2xl overflow-hidden bg-[hsl(var(--surface-elevated))]"
        style={{ border: "1px solid rgba(200, 160, 99, 0.18)" }}
      >
        <OfflineImg
          src={thumb}
          alt=""
          category={b.category}
          className="place-row-thumb transition-transform duration-700 group-hover:scale-[1.06]"
        />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <div className="font-display text-[1.4rem] text-foreground truncate leading-tight">
            {b.name}
          </div>
          {b.vip_enabled && <Crown className="w-4 h-4 text-gold shrink-0" strokeWidth={2} />}
        </div>
        <div className="text-[12px] text-foreground/55 mt-1 tracking-wide">
          <CategoryLabel category={b.category} subcategory={b.subcategory} />
          {(b.neighborhood || distance) && (
            <span>{" · "}{[b.neighborhood, distance].filter(Boolean).join(" · ")}</span>
          )}
        </div>
        <div className="mt-2 flex items-center gap-2">
          {b.ratings_average != null ? (
            <>
              <Stars value={b.ratings_average} size={12} />
              <span className="text-[11px] font-mono text-foreground/70">
                {b.ratings_average.toFixed(2)}{" "}
                <span className="text-foreground/45">({b.approved_review_count ?? b.ratings_count ?? 0})</span>
              </span>
            </>
          ) : (
            <OpenBadge business={b} />
          )}
        </div>
      </div>
    </Link>
  );
}

export { categoryLabel };
